// Períodos de reporte en la zona horaria del negocio (no la del dispositivo): "hoy" en un
// kiosco de Argentina va de 00:00 a 24:00 hora argentina aunque la PC tenga otra zona.

const DAY = 86_400_000;

/** Minutos que la zona horaria está adelantada respecto de UTC en ese instante (Argentina: −180). */
export function offsetMinutes(timeZone: string, at: Date): number {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(at)
      .map((p) => [p.type, p.value]),
  );
  const asUtc = Date.UTC(+parts.year!, +parts.month! - 1, +parts.day!, +parts.hour!, +parts.minute!, +parts.second!);
  return Math.round((asUtc - Math.floor(at.getTime() / 1000) * 1000) / 60_000);
}

/** Fecha local "AAAA-MM-DD" de un instante en la zona dada. */
export function localDate(at: Date, timeZone: string): string {
  return new Date(at.getTime() + offsetMinutes(timeZone, at) * 60_000).toISOString().slice(0, 10);
}

/** Instante en que empieza ese día (00:00) en la zona dada. */
export function startOfLocalDay(ymd: string, timeZone: string): Date {
  const [y, m, d] = ymd.split('-').map(Number) as [number, number, number];
  const guess = Date.UTC(y, m - 1, d);
  let at = new Date(guess - offsetMinutes(timeZone, new Date(guess)) * 60_000);
  // Segunda pasada por si el cambio de horario cae justo ese día.
  at = new Date(guess - offsetMinutes(timeZone, at) * 60_000);
  return at;
}

export function addDays(ymd: string, days: number): string {
  return new Date(Date.parse(`${ymd}T00:00:00Z`) + days * DAY).toISOString().slice(0, 10);
}

export type PeriodKey = 'today' | 'yesterday' | 'last7' | 'last30' | 'thisMonth' | 'lastMonth' | 'custom';

export const PERIOD_LABELS: Record<PeriodKey, string> = {
  today: 'Hoy',
  yesterday: 'Ayer',
  last7: 'Últimos 7 días',
  last30: 'Últimos 30 días',
  thisMonth: 'Este mes',
  lastMonth: 'Mes anterior',
  custom: 'Elegir fechas',
};

export interface Period {
  /** Primer día (local, incluido) y último día (local, incluido). */
  firstDay: string;
  lastDay: string;
  /** Rango para el servidor: [from, to). */
  from: string;
  to: string;
  /** Todos los días del período (para completar el gráfico con ceros). */
  days: string[];
}

export function makePeriod(firstDay: string, lastDay: string, timeZone: string): Period {
  const days: string[] = [];
  for (let d = firstDay; d <= lastDay && days.length < 400; d = addDays(d, 1)) days.push(d);
  return {
    firstDay,
    lastDay,
    from: startOfLocalDay(firstDay, timeZone).toISOString(),
    to: startOfLocalDay(addDays(lastDay, 1), timeZone).toISOString(),
    days,
  };
}

export function presetPeriod(key: Exclude<PeriodKey, 'custom'>, timeZone: string, now: Date = new Date()): Period {
  const today = localDate(now, timeZone);
  const monthStart = `${today.slice(0, 8)}01`;
  switch (key) {
    case 'today':
      return makePeriod(today, today, timeZone);
    case 'yesterday':
      return makePeriod(addDays(today, -1), addDays(today, -1), timeZone);
    case 'last7':
      return makePeriod(addDays(today, -6), today, timeZone);
    case 'last30':
      return makePeriod(addDays(today, -29), today, timeZone);
    case 'thisMonth':
      return makePeriod(monthStart, today, timeZone);
    case 'lastMonth': {
      const lastOfPrev = addDays(monthStart, -1);
      return makePeriod(`${lastOfPrev.slice(0, 8)}01`, lastOfPrev, timeZone);
    }
  }
}

/** Variación porcentual (null si no hay base para comparar). */
export function change(current: number, previous: number): number | null {
  if (!previous) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
}
